// noinspection DuplicatedCode
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "@material-tailwind/react/Modal";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import Button from "@material-tailwind/react/Button";
import Card from "@material-tailwind/react/Card";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

import { isRequired, useForm } from "../../../../components/isRequired";
import Loading from "../../../../components/loading/Loading";
import Skeltons from "../../../../components/loading/Skeltons";
import { Colors } from "../../../../assets/Colors";
import { KaryawanSelectorBuilder } from "../../karyawan/KaryawanSelector";

import {
  getPromosiTransaksi,
  insertPromosiTransaksi,
  updatePromosiTransaksi,
  deletePromosiTransaksi,
  getSpecPromosi,
  promosiTransaksiSelector,
} from "./promosiTransaksiSlice";
import { approve } from "../../../setup/approval/approvalSlice";
import { getMinJabatan, jabatanSelector } from "../../../master/jabatan/jabatanSlice";
import {
  getMinLokasiTugas,
  lokasiTugasSelector,
} from "../../../master/lokasi-tugas/lokasiTugasSlice";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// JenisTransaksi (kode tanpa lookup table — konfirmasi bersama user, lihat
// backend models/hrd/promotion.go): 1=Promosi, 2=Mutasi, 3=Demosi.
const JENIS_TRANSAKSI_OPTIONS = [
  { value: "1", label: "Promosi (naik jabatan)" },
  { value: "2", label: "Mutasi (pindah lokasi tugas)" },
  { value: "3", label: "Demosi (turun jabatan)" },
];
const jenisTransaksiLabel = (v) =>
  JENIS_TRANSAKSI_OPTIONS.find((o) => o.value === String(v))?.label || v;

const toDateInputStr = (d) =>
  d instanceof Date && !isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : "";

const PromosiPage = () => {
  const initialState = { Nik: "", JenisTransaksi: "", PosisiBaru: "", Alasan: "" };
  const validations = [
    ({ Nik }) => isRequired(Nik) || { Nik: "Karyawan wajib dipilih" },
    ({ JenisTransaksi }) =>
      isRequired(JenisTransaksi) || { JenisTransaksi: "Jenis transaksi wajib dipilih" },
    ({ PosisiBaru }) => isRequired(PosisiBaru) || { PosisiBaru: "Posisi baru wajib dipilih" },
    ({ Alasan }) => isRequired(Alasan) || { Alasan: "Alasan wajib diisi" },
  ];
  const { values, errors, touched, changeHandler, customHandler, editHandler, clearHandler, multiFieldHandler } =
    useForm(initialState, validations);
  const [buttonTouched, setButtonTouched] = useState(false);

  const dispatch = useDispatch();
  const promosi = useSelector(promosiTransaksiSelector);
  const jabatan = useSelector(jabatanSelector);
  const lokasiTugas = useSelector(lokasiTugasSelector);

  const [tanggalEfektif, setTanggalEfektif] = useState("");
  const [isValidTanggal, setIsValidTanggal] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [currentData, setCurrentData] = useState({});
  const [showModalDetail, setShowModalDetail] = useState(false);
  const [detailID, setDetailID] = useState("");
  const [detailRow, setDetailRow] = useState({});

  const refetchList = (searchValue = searchQuery) =>
    dispatch(getPromosiTransaksi({ start: 0, count: 200, search: searchValue }));

  useEffect(() => {
    refetchList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data list (client-side search + pagination, mengikuti pola Lembur) ──
  const allRows = useMemo(() => (Array.isArray(promosi.data) ? promosi.data : []), [promosi.data]);
  const filtered = useMemo(() => {
    const kw = searchQuery.trim().toLowerCase();
    if (!kw) return allRows;
    return allRows.filter((r) =>
      [r.TPromosiID, r.NamaKaryawan, r.Alasan, r.StatusKet].some(
        (v) => v && v.toString().toLowerCase().includes(kw)
      )
    );
  }, [allRows, searchQuery]);
  const PAGE_SIZE = 10;
  const totalPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPage);
  const pageRows = useMemo(() => {
    const s = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(s, s + PAGE_SIZE);
  }, [filtered, safePage]);

  // ── Export (mengikuti hasil filter/search) ───────────────────
  const exportHeader = ["ID", "Nama Karyawan", "Jenis", "Posisi Baru", "Tanggal Efektif", "Alasan", "Status"];
  const exportBody = () =>
    filtered.map((r) => [
      r.TPromosiID || "", r.NamaKaryawan || "", jenisTransaksiLabel(r.JenisTransaksi), r.BagianBaru || "",
      r.TanggalEfektif || "", r.Alasan || "", r.StatusKet || "",
    ]);
  const handleDownloadExcel = () => {
    downloadExcel({ fileName: "Promosi_Mutasi", sheet: "data", tablePayload: { header: exportHeader, body: exportBody() } });
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(13);
    doc.text("Promosi / Mutasi", 14, 14);
    autoTable(doc, { head: [exportHeader], body: exportBody(), startY: 20, styles: { fontSize: 7 }, headStyles: { fillColor: [30, 58, 95] } });
    doc.save("Promosi_Mutasi.pdf");
  };

  const openTambah = () => {
    clearHandler();
    setTanggalEfektif("");
    setIsValidTanggal(true);
    setButtonTouched(false);
    setCurrentData({});
    setShowModalEdit(false);
    setShowModalAdd(true);
    dispatch(getMinJabatan({ search: "" }));
    dispatch(getMinLokasiTugas({ search: "" }));
  };
  const openEdit = (row) => {
    clearHandler();
    editHandler({
      Nik: row.Nik,
      JenisTransaksi: String(row.JenisTransaksi),
      PosisiBaru: row.PosisiBaru,
      Alasan: row.Alasan,
    });
    setTanggalEfektif(row.TanggalEfektif ? new Date(row.TanggalEfektif) : "");
    setCurrentData(row);
    setShowModalEdit(true);
    setShowModalAdd(true);
    dispatch(getMinJabatan({ search: "" }));
    dispatch(getMinLokasiTugas({ search: "" }));
  };
  const closeModal = () => {
    setShowModalAdd(false);
    setShowModalEdit(false);
    clearHandler();
    setTanggalEfektif("");
    setButtonTouched(false);
  };

  const submit = () => {
    setButtonTouched(true);
    if (!values.Nik || !values.JenisTransaksi || !values.PosisiBaru || !values.Alasan) return;
    if (!tanggalEfektif) {
      setIsValidTanggal(false);
      return;
    }
    const payload = {
      Nik: values.Nik,
      JenisTransaksi: values.JenisTransaksi,
      PosisiBaru: values.PosisiBaru,
      TanggalEfektif: toDateInputStr(tanggalEfektif),
      Alasan: values.Alasan,
    };
    if (showModalEdit) {
      dispatch(updatePromosiTransaksi({ ...payload, TPromosiID: currentData.TPromosiID })).then(
        (res) => {
          if (!res.error) {
            toast.success("Data diperbarui", { position: "bottom-right", autoClose: 3000 });
            closeModal();
            refetchList();
          } else {
            toast.error(res.payload?.message || "Gagal memperbarui data", {
              position: "bottom-right",
            });
          }
        }
      );
    } else {
      dispatch(insertPromosiTransaksi(payload)).then((res) => {
        if (!res.error) {
          toast.success("Pengajuan dibuat", { position: "bottom-right", autoClose: 3000 });
          closeModal();
          refetchList();
        } else {
          toast.error(res.payload?.message || "Gagal membuat pengajuan", {
            position: "bottom-right",
          });
        }
      });
    }
  };

  const doSend = async (row) => {
    try {
      const res = await dispatch(approve({ primary: row.TPromosiID })).unwrap();
      if (res && res.data === "Berhasil") {
        toast.success("Dikirim untuk approval", { position: "bottom-right", autoClose: 3000 });
      } else {
        toast.error((res && res.message) || "Gagal mengirim", { position: "bottom-right" });
      }
    } catch (e) {
      toast.error((e && e.message) || "Gagal mengirim untuk approval", {
        position: "bottom-right",
      });
    }
    refetchList();
  };
  const doDetail = (row) => {
    setDetailID(row.TPromosiID);
    setDetailRow(row);
    dispatch(getSpecPromosi({ TransaksiApprovalID: row.TPromosiID }));
    setShowModalDetail(true);
  };
  const doDelete = (row) => {
    if (!window.confirm(`Hapus pengajuan ${row.TPromosiID} (${row.NamaKaryawan})?`)) return;
    dispatch(deletePromosiTransaksi({ TPromosiID: row.TPromosiID })).then((res) => {
      if (!res.error) {
        toast.success("Data dihapus", { position: "bottom-right", autoClose: 3000 });
        refetchList();
      } else {
        toast.error(res.payload?.message || "Gagal menghapus", { position: "bottom-right" });
      }
    });
  };

  const statusClass = (s) =>
    s === "Selesai"
      ? "bg-green-100 text-green-800"
      : s === "Belum Dikirim"
      ? "bg-gray-100 text-gray-700"
      : s === "Ditolak"
      ? "bg-red-100 text-red-800"
      : "bg-yellow-100 text-yellow-800";

  const colHeader = [
    "ID", "Nama Karyawan", "Jenis", "Posisi Baru", "Tanggal Efektif", "Alasan", "Status", "Aksi",
  ];

  // getMinJabatan (jabatanSlice) sudah mengubah hasilnya jadi {value,label} sendiri,
  // sedangkan getMinLokasiTugas mengembalikan bentuk mentah {LokasiTugasID,NamaLokasiTugas}
  // — dua sumber, dua bentuk berbeda, jangan disamaratakan.
  const posisiOptions =
    values.JenisTransaksi === "2"
      ? (lokasiTugas?.minData || []).map((x) => ({ value: x.LokasiTugasID, label: x.NamaLokasiTugas }))
      : (jabatan?.minData || []).map((x) => ({ value: x.value, label: x.label }));

  return (
    <>
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>
                {promosi.isGetFetching ? (
                  <div className="w-full pt-0">
                    <Skeltons />
                  </div>
                ) : (
                  <>
                    <div className="flex w-full justify-between px-4 pt-4 flex-col sm:flex-row gap-2">
                      <div className="flex flex-row gap-2 flex-wrap">
                        <button
                          onClick={openTambah}
                          className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                        >
                          Tambah +
                        </button>
                        <button onClick={handleDownloadExcel}
                          className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-green-600 hover:bg-green-600 hover:text-white rounded shadow">
                          Download Excel
                        </button>
                        <button onClick={handleDownloadPDF}
                          className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow">
                          Download PDF
                        </button>
                      </div>
                      <div className="flex flex-row w-full sm:w-96">
                        <input
                          type="text"
                          placeholder="Cari ID / Nama karyawan"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                        />
                      </div>
                    </div>

                    <div className="overflow-x-auto px-4 pt-4">
                      <table className="table-auto w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-orange-600 uppercase bg-gray-50">
                          <tr>
                            {colHeader.map((h, i) => (
                              <th key={i} className="py-3 px-6">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.length === 0 ? (
                            <tr>
                              <td colSpan={colHeader.length} className="text-center py-8 text-gray-400">
                                Belum ada data promosi/mutasi
                              </td>
                            </tr>
                          ) : (
                            pageRows.map((row, i) => {
                                const isDraft = String(row.Status) === "-1";
                                return (
                                  <tr
                                    key={row.TPromosiID}
                                    className={i % 2 === 0 ? "bg-gray-50 border-b" : "bg-white border-b"}
                                  >
                                    <td className="py-4 px-6 font-mono text-xs">{row.TPromosiID}</td>
                                    <td className="py-4 px-6">{row.NamaKaryawan}</td>
                                    <td className="py-4 px-6">{jenisTransaksiLabel(row.JenisTransaksi)}</td>
                                    <td className="py-4 px-6">{row.BagianBaru || "-"}</td>
                                    <td className="py-4 px-6 whitespace-nowrap">{row.TanggalEfektif || "-"}</td>
                                    <td className="py-4 px-6">{row.Alasan || "-"}</td>
                                    <td className="py-4 px-6">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClass(
                                          row.StatusKet
                                        )}`}
                                      >
                                        {row.StatusKet}
                                      </span>
                                    </td>
                                    <td className="py-4 px-6 whitespace-nowrap">
                                      <div className="flex gap-2">
                                        {isDraft && (
                                          <>
                                            <button
                                              onClick={() => openEdit(row)}
                                              className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-1 px-3 border border-gray-400 rounded shadow text-sm"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={() => doSend(row)}
                                              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 rounded shadow text-sm"
                                            >
                                              Kirim
                                            </button>
                                            <button
                                              onClick={() => doDelete(row)}
                                              className="bg-white hover:bg-red-50 text-red-600 font-semibold py-1 px-3 border border-red-300 rounded shadow text-sm"
                                            >
                                              Hapus
                                            </button>
                                          </>
                                        )}
                                        {!isDraft && (
                                          <button
                                            onClick={() => doDetail(row)}
                                            className="bg-white hover:bg-blue-50 text-blue-600 font-semibold py-1 px-3 border border-blue-300 rounded shadow text-sm"
                                          >
                                            Detail
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-row justify-between items-center gap-4 px-4 py-4 text-sm text-gray-600">
                      <span>{filtered.length} data</span>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={safePage <= 1}
                          className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                          Prev
                        </button>
                        <span>
                          Page {safePage} / {totalPage}
                        </span>
                        <button
                          onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                          disabled={safePage >= totalPage}
                          className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal Tambah/Edit ── */}
      <Modal size="regular" active={showModalAdd} toggler={() => setShowModalAdd(true)}>
        <ModalHeader className="mt-20" toggler={closeModal}>
          {showModalEdit ? "Edit Promosi/Mutasi" : "Tambah Promosi/Mutasi"}
        </ModalHeader>
        <ModalBody>
          <form className="w-full max-w-lg">
            <KaryawanSelectorBuilder value={values.Nik} onChange={customHandler} noTlpn />

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Jenis Transaksi *
                </label>
                <select
                  value={values.JenisTransaksi}
                  onChange={(e) => {
                    // Dua customHandler() berturut-turut saling menimpa (closure
                    // `values` basi) — pakai multiFieldHandler agar diterapkan
                    // sekaligus. Lihat [[ranji-report-page-standar]].
                    multiFieldHandler({ JenisTransaksi: e.target.value, PosisiBaru: "" });
                  }}
                  className={
                    (touched.JenisTransaksi || buttonTouched) && errors.JenisTransaksi
                      ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                      : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  }
                >
                  <option value="">-- Pilih --</option>
                  {JENIS_TRANSAKSI_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {(touched.JenisTransaksi || buttonTouched) && errors.JenisTransaksi && (
                  <p className="text-red-500 text-xs italic">{errors.JenisTransaksi}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  {values.JenisTransaksi === "2" ? "Lokasi Tugas Baru *" : "Jabatan Baru *"}
                </label>
                <select
                  value={values.PosisiBaru}
                  disabled={!values.JenisTransaksi}
                  onChange={(e) => customHandler(e.target.value, "PosisiBaru")}
                  className={
                    (touched.PosisiBaru || buttonTouched) && errors.PosisiBaru
                      ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                      : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  }
                >
                  <option value="">-- Pilih --</option>
                  {posisiOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {(touched.PosisiBaru || buttonTouched) && errors.PosisiBaru && (
                  <p className="text-red-500 text-xs italic">{errors.PosisiBaru}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Efektif *
                </label>
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={tanggalEfektif || null}
                  onChange={(d) => {
                    setTanggalEfektif(d);
                    setIsValidTanggal(true);
                  }}
                  className={
                    !isValidTanggal
                      ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 leading-tight outline-none bg-white"
                      : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  }
                />
                {!isValidTanggal && (
                  <p className="text-red-500 text-xs italic">Tanggal efektif wajib diisi</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap -mx-3 mb-2">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan *
                </label>
                <input
                  value={values.Alasan}
                  onChange={changeHandler}
                  name="Alasan"
                  type="text"
                  className={
                    (touched.Alasan || buttonTouched) && errors.Alasan
                      ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                      : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  }
                />
                {(touched.Alasan || buttonTouched) && errors.Alasan && (
                  <p className="text-red-500 text-xs italic">{errors.Alasan}</p>
                )}
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="red" buttonType="link" onClick={closeModal} ripple="dark">
            Batal
          </Button>
          <Button color="green" onClick={submit} ripple="light">
            SIMPAN
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal Detail: snapshot lama + riwayat approval ── */}
      <Modal size="regular" active={showModalDetail} toggler={() => setShowModalDetail(true)}>
        <ModalHeader
          className="mt-20"
          toggler={() => {
            setShowModalDetail(false);
            setDetailID("");
          }}
        >
          Detail Promosi/Mutasi
        </ModalHeader>
        <ModalBody>
          {promosi.SpecPromosiData && promosi.SpecPromosiData.TPromosiID === detailID ? (
            <div className="w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Nama Karyawan</div>
                  <div className="text-gray-800">{detailRow.NamaKaryawan || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Jenis Transaksi</div>
                  <div className="text-gray-800">{jenisTransaksiLabel(detailRow.JenisTransaksi)}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Jabatan Lama → Baru</div>
                  <div className="text-gray-800">
                    {promosi.SpecPromosiData.JabatanLama || "-"}
                    {detailRow.JenisTransaksi !== "2" && (
                      <> → {promosi.SpecPromosiData.BagianBaru || "-"}</>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Bagian Lama</div>
                  <div className="text-gray-800">{promosi.SpecPromosiData.BagianLama || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Golongan Lama</div>
                  <div className="text-gray-800">{promosi.SpecPromosiData.GolonganLama || "-"}</div>
                </div>
                {detailRow.JenisTransaksi === "2" && (
                  <div>
                    <div className="text-xs font-bold uppercase text-gray-500">Lokasi Tugas Baru</div>
                    <div className="text-gray-800">{promosi.SpecPromosiData.BagianBaru || "-"}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Tanggal Efektif</div>
                  <div className="text-gray-800">{promosi.SpecPromosiData.TanggalEfektif || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-500">Status</div>
                  <div className="text-gray-800">{promosi.SpecPromosiData.StatusKet || "-"}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs font-bold uppercase text-gray-500">Alasan</div>
                  <div className="text-gray-800">{promosi.SpecPromosiData.Alasan || "-"}</div>
                </div>
              </div>

              <div className="border-t pt-3 mb-2 font-semibold text-gray-700">Riwayat Approval</div>
              {Array.isArray(promosi.SpecPromosiData.Approval) &&
              promosi.SpecPromosiData.Approval.length > 0 ? (
                <div className="space-y-2">
                  {promosi.SpecPromosiData.Approval.map((a, ai) => {
                    const badge =
                      a.Approve === "Ditolak"
                        ? "bg-red-100 text-red-800"
                        : a.Approve === "Sudah diApprove"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600";
                    return (
                      <div key={ai} className="border rounded px-4 py-3 flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-800">
                            {a.NamaBagian} — {a.NamaJabatan}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${badge}`}
                          >
                            {a.Approve}
                          </span>
                        </div>
                        {a.Approve !== "Belum diApprove" && a.TanggalApproved && (
                          <div className="text-xs text-gray-600">{a.TanggalApproved}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-400 text-sm py-2">Belum ada data approval.</div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="blueGray"
            buttonType="link"
            onClick={() => {
              setShowModalDetail(false);
              setDetailID("");
            }}
            ripple="dark"
          >
            Tutup
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PromosiPage;
